'use client';
import { getCTA } from '@/utils/getCTA';
import { getColorScheme } from '@/utils/getColorScheme';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import IconValue from './atoms/IconValue';
import { MarkDown } from './atoms/MarkDown';

function normalizeAlignment(val) {
    const raw =
        (val && typeof val === 'object' && 'value' in val ? val.value : val) ??
        'center';
    const v = String(raw).toLowerCase().trim();
    if (v === 'left') return 'left';
    if (v === 'right') return 'right';
    return 'center';
}

function coerceBoolean(value, fallback = true) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    }
    return fallback;
}

export default function Popup(props) {
    const {
        Enabled = true,
        Icon,
        Header,
        Body,
        CTA,
        CustomCTALink,
        HideCTAButton = false,
        Image,
        SecondaryButtonText,
        SecondaryButtonLink,
        OutlineButtonText,
        OutlineButtonLink,
        ColorScheme = 'white-primary',
        Alignment = 'center',
        Shadow = 'true',
        is_edit_mode,
        ImagePosition = 'top',
        ImageObjectPosition = '50',
        Timelapse = 5000,
        Position = 'center',
        editor_component_file,
    } = props;
    const [open, setOpen] = useState(false);
    const enabled = coerceBoolean(Enabled, true);
    const {
        backgroundColor,
        textColor,
        headerColor,
        iconColor,
        thirdButton,
        secondaryButton,
        exitButton,
        bgOverlay,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const normalizedAlignment = normalizeAlignment(Alignment);
    let textAlign, buttonAlign;
    switch (normalizedAlignment) {
        case 'left':
            textAlign = 'text-center md:text-left max-w-3xl md:mr-auto md:ml-0';
            buttonAlign = 'justify-center md:justify-start';
            break;
        case 'right':
            textAlign =
                'text-center md:text-right max-w-3xl md:ml-auto md:mr-0';
            buttonAlign = 'justify-center md:justify-end';
            break;
        default: // center
            textAlign = 'text-center mx-auto items-center';
            buttonAlign = 'justify-center';
            break;
    }

    let shadowBox;
    switch (Shadow) {
        case 'false':
            shadowBox = '';
            break;
        default: // true
            shadowBox = 'shadow-xl';
            break;
    }

    useEffect(() => {
        if (!enabled || is_edit_mode) return undefined;

        const timer = setTimeout(() => {
            setOpen(true);
        }, Timelapse);

        return () => clearTimeout(timer); // Clean up the timer
    }, [enabled, is_edit_mode, Timelapse]);

    useEffect(() => {
        if (!is_edit_mode || typeof window === 'undefined') return undefined;

        const previewPopup = (event) => {
            const targetFile = event?.detail?.file;
            if (
                targetFile &&
                editor_component_file &&
                targetFile !== editor_component_file
            ) {
                return;
            }

            setOpen(true);
        };

        window.addEventListener('editor:preview-popup', previewPopup);
        return () =>
            window.removeEventListener('editor:preview-popup', previewPopup);
    }, [editor_component_file, is_edit_mode]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const openPopup = () => setOpen(true);
        window.addEventListener('site:open-popup', openPopup);
        return () => window.removeEventListener('site:open-popup', openPopup);
    }, []);

    const rawImagePosition =
        ImagePosition &&
        typeof ImagePosition === 'object' &&
        'value' in ImagePosition
            ? ImagePosition.value
            : ImagePosition;
    const rawCustomCTALink =
        CustomCTALink &&
        typeof CustomCTALink === 'object' &&
        'value' in CustomCTALink
            ? CustomCTALink.value
            : CustomCTALink;
    const hideCTAButton = coerceBoolean(HideCTAButton, false);
    const hasPrimaryLink = Boolean(String(rawCustomCTALink ?? '').trim());
    const useHiddenPrimaryCTAAction = hideCTAButton;
    const usePopupLinkAsPrimaryAction =
        useHiddenPrimaryCTAAction && hasPrimaryLink;
    const usePopupNiceJobAsPrimaryAction =
        useHiddenPrimaryCTAAction && !hasPrimaryLink;
    const popupLinkHref = usePopupLinkAsPrimaryAction
        ? String(rawCustomCTALink).trim()
        : '';
    const normalizedImagePosition = (() => {
        const v = String(rawImagePosition || '')
            .toLowerCase()
            .trim();
        if (v === 'full' || v === 'image-full' || v === 'image full') {
            return 'full';
        }
        if (v === 'center') return 'top';
        if (!v) return 'top';
        return v;
    })();
    const isFullImageLayout = normalizedImagePosition === 'full';
    const isBottomFullImageLayout = isFullImageLayout && Position === 'bottom';
    const popupImageAlt =
        typeof Header === 'string' && Header.trim() ? Header : 'Popup image';
    const { x: imagePosX, y: imagePosY } =
        parseImagePosition(ImageObjectPosition);
    const imageObjectPosition = `${imagePosX}% ${imagePosY}%`;

    let imageLayout, imageShape;
    switch (normalizedImagePosition) {
        case 'full':
            imageLayout = 'flex-col';
            imageShape = 'w-full';
            break;
        case 'left':
            imageLayout = 'flex-row';
            imageShape =
                'rounded-l-[min(1.5rem,var(--shape-radius))] w-1/3 h-full';
            break;
        case 'right':
            imageLayout = 'flex-row-reverse';
            imageShape =
                'rounded-r-[min(1.5rem,var(--shape-radius))] w-1/3 h-full';
            break;
        case 'bottom':
            imageLayout = 'flex-col-reverse';
            imageShape =
                'rounded-b-[min(1.5rem,var(--shape-radius))] w-full h-64';
            break;
        default: // top
            imageLayout = 'flex-col';
            imageShape =
                'rounded-t-[min(1.5rem,var(--shape-radius))] w-full h-64';
            break;
    }

    let positionClasses,
        viewportClasses,
        boxPadding,
        iconSize,
        boxSize,
        headerSize,
        bodySize,
        imageSize,
        buttonSize,
        buttonMargin;
    switch (Position) {
        case 'left-corner':
            viewportClasses =
                'fixed inset-0 z-[1000] flex items-end justify-start p-4';
            positionClasses = '';
            boxPadding = isFullImageLayout ? '' : ' p-3 lg:p-6';
            iconSize = 'mb-2 text-2xl';
            boxSize = isFullImageLayout
                ? 'w-fit max-w-[min(92vw,30rem)]'
                : 'max-w-xs lg:max-w-sm';
            headerSize = 'text-2xl';
            bodySize = 'text-sm leading-6';
            imageSize =
                normalizedImagePosition === 'bottom' ||
                normalizedImagePosition === 'top'
                    ? 'h-36'
                    : '';
            buttonSize = 'sm';
            buttonMargin = isFullImageLayout ? '' : 'mt-8';
            break;
        case 'right-corner':
            viewportClasses =
                'fixed inset-0 z-[1000] flex items-end justify-end p-4';
            positionClasses = '';
            boxPadding = isFullImageLayout ? '' : ' p-3 lg:p-6';
            iconSize = 'mb-2 text-2xl';
            boxSize = isFullImageLayout
                ? 'w-fit max-w-[min(92vw,30rem)]'
                : 'max-w-xs lg:max-w-sm';
            headerSize = 'text-2xl';
            bodySize = 'text-sm leading-6';
            imageSize =
                normalizedImagePosition === 'bottom' ||
                normalizedImagePosition === 'top'
                    ? 'h-36'
                    : '';
            buttonSize = 'sm';
            buttonMargin = isFullImageLayout ? '' : 'mt-8';
            break;
        case 'bottom':
            viewportClasses =
                'fixed inset-0 z-[1000] flex items-end justify-center';
            positionClasses = isBottomFullImageLayout
                ? 'w-full'
                : isFullImageLayout
                ? ''
                : 'w-full';
            boxPadding = isFullImageLayout ? '' : ' p-3 lg:p-6';
            iconSize = 'mb-2 text-2xl';
            boxSize = isBottomFullImageLayout
                ? 'w-full'
                : isFullImageLayout
                ? 'w-auto max-w-full'
                : 'w-full';
            headerSize = 'text-2xl';
            bodySize = 'text-sm leading-6';
            imageSize = 'hidden';
            buttonSize = 'sm';
            buttonMargin = isFullImageLayout ? '' : 'mt-8';
            break;
        default: // center
            viewportClasses =
                'fixed inset-0 z-[1000] flex items-center justify-center p-3';
            positionClasses = '';
            boxPadding = isFullImageLayout ? '' : ' p-8 lg:p-16';
            iconSize = 'mb-6 text-4xl lg:text-6xl';
            boxSize = isFullImageLayout
                ? 'w-fit max-w-[min(92vw,72rem)]'
                : ' max-w-3xl max-h-full';
            headerSize = 'text-2xl md:text-3xl lg:text-4xl';
            bodySize = 'text-base leading-7 ';
            buttonSize = 'md';
            buttonMargin = isFullImageLayout ? '' : 'mt-10 lg:mt-16 ';
            break;
    }

    const closePopup = () => {
        setOpen(false);
    };

    const handleCTAButtonClick = () => {
        closePopup();
        if (hasPrimaryLink) return;
        // Trigger the nj-lead functionality after the popup is closed
        setTimeout(() => {
            const njLeadButton = document.querySelector('.nj-lead');
            if (njLeadButton) {
                njLeadButton.click(); // Simulate the click on the nj-lead button
            }
        }, 300); // Delay to ensure the popup is closed first
    };

    const { hasAny: hasCTA, element: CTAButtons } = getCTA(
        {
            ...props,
            CTA: useHiddenPrimaryCTAAction ? '' : CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign: isFullImageLayout ? 'justify-center' : buttonAlign,
            wrapSpacing: buttonMargin,
            className: `w-full ${
                useHiddenPrimaryCTAAction ? 'pointer-events-auto' : ''
            }`,
            buttonsClassName: `flex flex-wrap items-center gap-2 ${
                isFullImageLayout ? 'justify-center' : ''
            }`,
            size: buttonSize,
            primarySize: 'md',
            primaryButtonProps: { onClick: handleCTAButtonClick },
            secondaryButtonProps: { onClick: closePopup },
            outlineButtonProps: { onClick: closePopup },
        }
    );

    return (
        <div>
            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className={`${viewportClasses} ${
                                Position === 'center' ? bgOverlay : ''
                            }`}
                            onClick={closePopup}
                        >
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y:
                                        Position === 'left-corner' ||
                                        Position === 'right-corner' ||
                                        Position === 'bottom'
                                            ? '100%'
                                            : 0,
                                    scale: Position === 'center' ? 0.9 : 1,
                                }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{
                                    opacity: 0,
                                    y:
                                        Position === 'left-corner' ||
                                        Position === 'right-corner' ||
                                        Position === 'bottom'
                                            ? '100%'
                                            : 0,
                                    scale: Position === 'center' ? 0.9 : 1,
                                }}
                                transition={{
                                    duration: 0.5,
                                    ease: [0.25, 0.1, 0.25, 1], // easeInOutCubic for a smoother effect
                                }}
                                className={`${backgroundColor} ${shadowBox} ${boxSize} relative grid ${
                                    Position === 'bottom'
                                        ? 'rounded-none'
                                        : 'rounded-[min(1.5rem,var(--shape-radius))]'
                                } ${positionClasses} ${
                                    useHiddenPrimaryCTAAction
                                        ? 'cursor-pointer'
                                        : ''
                                }`}
                                style={{
                                    ...backgroundStyle,
                                    pointerEvents: 'auto',
                                    maxHeight: '90vh',
                                    overflow: isFullImageLayout
                                        ? 'hidden'
                                        : 'auto',
                                }}
                                onClick={(event) => event.stopPropagation()}
                            >
                                {usePopupLinkAsPrimaryAction && (
                                    <a
                                        href={popupLinkHref}
                                        aria-label={
                                            Header
                                                ? `Open ${Header}`
                                                : 'Open popup link'
                                        }
                                        className="absolute inset-0 z-0"
                                    />
                                )}
                                {usePopupNiceJobAsPrimaryAction && (
                                    <button
                                        type="button"
                                        aria-label={
                                            Header
                                                ? `Open ${Header}`
                                                : 'Open popup action'
                                        }
                                        className="absolute inset-0 z-0"
                                        onClick={handleCTAButtonClick}
                                    />
                                )}
                                <div
                                    className={`relative ${
                                        useHiddenPrimaryCTAAction
                                            ? 'pointer-events-none z-10'
                                            : ''
                                    } flex ${imageLayout}`}
                                >
                                    {isFullImageLayout ? (
                                        <div
                                            className={`relative overflow-hidden bg-neutral-950/10 ${
                                                isBottomFullImageLayout
                                                    ? 'h-64 w-full'
                                                    : 'mx-auto w-fit max-w-full'
                                            }`}
                                        >
                                            {isBottomFullImageLayout ? (
                                                <div
                                                    aria-label={popupImageAlt}
                                                    role="img"
                                                    className="h-full w-full bg-cover bg-center"
                                                    style={{
                                                        backgroundImage: Image
                                                            ? `url(${Image})`
                                                            : undefined,
                                                        backgroundPosition:
                                                            imageObjectPosition,
                                                    }}
                                                />
                                            ) : Image ? (
                                                <img
                                                    src={Image}
                                                    alt={popupImageAlt}
                                                    className="block h-auto max-h-[90vh] w-auto max-w-full"
                                                    style={{
                                                        objectPosition:
                                                            imageObjectPosition,
                                                    }}
                                                />
                                            ) : (
                                                <div className="h-[16rem] w-[min(92vw,32rem)] max-w-full" />
                                            )}
                                            <div className="absolute right-0 top-0 z-20 pr-4 pt-4">
                                                <button
                                                    type="button"
                                                    className={`${exitButton} duration-900 pointer-events-auto transition-all ease-in focus:outline-none`}
                                                    onClick={closePopup}
                                                >
                                                    <span className="sr-only">
                                                        Close
                                                    </span>
                                                    <i
                                                        className="fa-solid fa-xmark h-8 w-8 text-2xl text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]"
                                                        aria-hidden="true"
                                                    ></i>
                                                </button>
                                            </div>
                                            {hasCTA && (
                                                <div className="absolute inset-x-0 bottom-0 z-20 p-4 sm:p-6">
                                                    {CTAButtons}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        Image && (
                                            <div
                                                className={`${imageShape} ${imageSize} relative bg-cover bg-center`}
                                                style={{
                                                    backgroundImage: `url(${Image})`,
                                                    backgroundPosition:
                                                        imageObjectPosition,
                                                }}
                                            ></div>
                                        )
                                    )}
                                    {!isFullImageLayout && (
                                        <div
                                            className={`flex-1 ${boxPadding} ${textAlign}`}
                                        >
                                            <div className="absolute right-0 top-0 z-20 pr-4 pt-4">
                                                <button
                                                    type="button"
                                                    className={`${exitButton} duration-900 pointer-events-auto transition-all ease-in focus:outline-none`}
                                                    onClick={closePopup}
                                                >
                                                    <span className="sr-only">
                                                        Close
                                                    </span>
                                                    <i
                                                        className="fa-solid fa-xmark h-8 w-8 text-2xl text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]"
                                                        aria-hidden="true"
                                                    ></i>
                                                </button>
                                            </div>

                                            {Icon && (
                                                <IconValue
                                                    value={Icon}
                                                    className={`mx-1 ${iconSize} ${iconColor}`}
                                                />
                                            )}
                                            {Header && (
                                                <MarkDown
                                                    variant="h2"
                                                    className={`font-display tracking-tight ${headerSize} ${headerColor}`}
                                                >
                                                    {Header}
                                                </MarkDown>
                                            )}
                                            {Body && (
                                                <MarkDown
                                                    className={`mt-6 font-text ${bodySize} ${textColor}`}
                                                >
                                                    {Body}
                                                </MarkDown>
                                            )}

                                            {hasCTA && CTAButtons}
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
